import { convexHull, minAreaOBB, obbCorners } from '../../../geo/polygon';
import type { OBB, Ring, Vec2 } from '../../../geo/types';
import { LASTAGE_YARD, yardFootprints } from '../../fabric/landmarks/lastageYard';
import { STOCKS_HULLS, stocksObb } from '../../harbour/stocks';

/**
 * Ground where work is done in the open and no street runs: the Lastage's yard, with the hulls on
 * its stocks and its timber (docs/plans/lastage.md, docs/plans/ships.md). The crowd walks it as it
 * walks a square, round what stands on it, and its hands stand at the hulls and the stacks.
 */
export interface WorkGround {
  readonly ring: Ring;
  /** What stands on the ground and is walked round. */
  readonly solid: readonly Ring[];
  /** What is worked at: its box, and how many hands at most to each long side of it. */
  readonly works: readonly { readonly box: OBB; readonly most: number }[];
}

export const YARD = {
  /** The yard's ground reaches this far past its hulls and its timber: an estimate, as the layout
   *  draws no edge for it. */
  marginM: 6,
  /** A hull's shores are walked round this far off. */
  hullClearM: 0.2,
  /** Timber that is sawn and stacked by hand; the heap and the kettle are not. */
  timber: ['boxStack', 'plankPile', 'logStack', 'beamHeap'],
} as const;

export function lastageYardGround(): WorkGround {
  const corners: Vec2[] = [];
  const solid: Ring[] = [];
  const works: { box: OBB; most: number }[] = [];
  for (const hull of STOCKS_HULLS) {
    corners.push(...obbCorners(stocksObb(hull, YARD.marginM)));
    solid.push([...obbCorners(stocksObb(hull, YARD.hullClearM))]);
    works.push({ box: stocksObb(hull, YARD.hullClearM), most: Infinity });
  }
  for (const item of LASTAGE_YARD) {
    for (const ring of yardFootprints(item, YARD.marginM)) corners.push(...ring);
    const stands = yardFootprints(item);
    solid.push(...stands);
    if ((YARD.timber as readonly string[]).includes(item.kind) && stands[0])
      works.push({ box: minAreaOBB(stands[0]), most: 1 });
  }
  return { ring: convexHull(corners), solid, works };
}
